import type { Id } from "@repo/convex/dataModel";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
  Layers,
  Square,
  X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { InlineReferenceMarkdown } from "@/components/ui/inline-reference-markdown";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageRichTextInput,
  type MessageRichTextSubmitPayload,
  type MessageRichTextVoiceInputConfig,
} from "@/features/rich-text";
import { cn } from "@/lib/utils";
import { getAssistantErrorMessage } from "../lib/ai-assistant-errors";
import type { AssistantAskUserPrompt, AssistantAskUserQuestionRuntime } from "../types";
import { getOptionLetter, OptionLetterBadge } from "./assistant-option-letter";
import {
  ASK_USER_CUSTOM_ANSWER_MAX,
  ASK_USER_CUSTOM_ANSWER_WARNING_RATIO,
  useAskUserDraft,
} from "./use-ask-user-draft";

/**
 * Option rows sit on the dock's `bg-muted` panel and are separated by a hairline, not a
 * full-strength `border-border` line: the design ("Chat — AskOptions A" in the Pencil file)
 * draws them in `$border-subtle` (#EFEFEF on #F5F4F2), a 1.05:1 step off the panel.
 * `border-border/40` reproduces that step on `bg-muted`; `border-border/60` is already twice
 * it, and plain `border-border` (1.15:1) reads as a full-strength divider.
 */
const OPTION_ROW_CLASS =
  "flex w-full items-center gap-3.5 border border-border/40 p-3 text-left transition-colors";

function getCustomAnswerPlaceholder(question: AssistantAskUserQuestionRuntime) {
  if (!question.placeholder || question.placeholder === "Type your answer...") {
    return "Your answer...";
  }

  return question.placeholder;
}

export interface AssistantAskUserDockStopControl {
  onStop: () => void;
  disabled: boolean;
  loading: boolean;
}

interface AssistantAskUserDockProps {
  projectId: Id<"projects">;
  /** The conversation the question was read from; travels with the answer. */
  threadId: string | null;
  request: AssistantAskUserPrompt;
  compact?: boolean;
  voiceInput?: MessageRichTextVoiceInputConfig;
  /** The composer's stop action, passed in whenever the composer would be offering one. */
  stop?: AssistantAskUserDockStopControl;
  onError?: (message: string) => void;
}

export function AssistantAskUserDock({
  projectId,
  threadId,
  request,
  compact = false,
  voiceInput,
  stop,
  onError,
}: AssistantAskUserDockProps) {
  const reportError = useCallback(
    (cause: unknown, fallback: string) => {
      const message = getAssistantErrorMessage(cause, fallback);
      onError?.(message);
      toast.error(message);
    },
    [onError]
  );
  const [voiceBusy, setVoiceBusy] = useState<{
    requestId: string;
    questionId: string;
  } | null>(null);
  const voiceBusyQuestionId =
    voiceBusy?.requestId === request.requestId ? voiceBusy.questionId : null;
  const draft = useAskUserDraft({
    projectId,
    threadId,
    request,
    reportError,
    isCurrentQuestionVoiceBusy: voiceBusyQuestionId !== null,
  });
  const questions = request.questions;
  const { questionIndex, currentQuestion } = draft;
  const currentAnswer = currentQuestion ? draft.answers[currentQuestion.id] : undefined;

  if (!currentQuestion) {
    return null;
  }

  const allowCustom = currentQuestion.allowCustom !== false;
  const hasDescriptions = currentQuestion.options.some((option) => Boolean(option.description));
  const customDraft = draft.customDrafts[currentQuestion.id] ?? {
    markdown: "",
    plainText: "",
  };
  const customAnswerText = customDraft.plainText.trim()
    ? customDraft.markdown.trim() || customDraft.plainText.trim()
    : "";
  const customAnswerLength = customAnswerText.length;
  const showCustomLimit =
    customAnswerLength >= ASK_USER_CUSTOM_ANSWER_MAX * ASK_USER_CUSTOM_ANSWER_WARNING_RATIO;
  const customLimitExceeded = customAnswerLength > ASK_USER_CUSTOM_ANSWER_MAX;
  const customInputId = `assistant-ask-user-custom-${request.requestId}-${currentQuestion.id}`;
  const customActive = draft.activeCustomQuestionId === currentQuestion.id;
  const customSelected =
    currentAnswer?.kind === "custom" || (customActive && currentAnswer?.kind !== "option");
  const customComposerActive =
    customActive && draft.composerSeed?.questionId === currentQuestion.id;
  const customAnswerPlaceholder = getCustomAnswerPlaceholder(currentQuestion);
  const controlsDisabled = draft.isResolving || draft.isClosed;
  const currentQuestionVoiceBusy = voiceBusyQuestionId === currentQuestion.id;
  const navigationDisabled = controlsDisabled || currentQuestionVoiceBusy;

  const submitCustomAnswer = async () => {
    if (customLimitExceeded) {
      return false;
    }
    return draft.submitCustomAnswer();
  };

  const handleCustomFocus = () => {
    draft.activateCustom(currentQuestion.id);
  };

  const handleCustomContentChange = (payload: MessageRichTextSubmitPayload) => {
    draft.setCustomText(currentQuestion.id, {
      markdown: payload.markdown,
      plainText: payload.plainText,
    });
  };

  return (
    // The dock takes the composer's place at the bottom of a flex column whose ancestors clip
    // overflow. It has to be able to shrink (`min-h-0`) so that, when the options do not fit,
    // the host gives it what is left above the transcript's floor and the options scroll
    // inside it. The nav row and the question stay pinned; only the answers scroll.
    <fieldset
      className={cn(
        "flex min-h-0 flex-col border-t border-border bg-muted text-foreground dark:bg-muted/40",
        compact ? "px-3.5 py-3" : "px-12 py-4"
      )}
      aria-label={`Question ${questionIndex + 1} of ${questions.length}`}
      data-testid="assistant-ask-user-dock"
    >
      <div className={cn("flex min-h-0 w-full flex-col gap-2", compact ? "" : "pl-9")}>
        <div className="flex shrink-0 flex-col gap-1 px-1.5 pb-1">
          <div className="flex shrink-0 items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Previous question"
              disabled={questionIndex === 0 || navigationDisabled}
              onClick={() => void draft.goPrev()}
              className="h-[18px] w-[18px] p-0 text-neutral-dim"
            >
              <ChevronLeft className="size-3" />
            </Button>
            <div className="inline-flex h-6 items-center gap-1.5 px-2 py-[3px] text-[11px] font-medium text-muted-foreground">
              <Layers className="size-3" />
              <span>
                Question {questionIndex + 1} of {questions.length}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Next question"
              disabled={questionIndex >= questions.length - 1 || navigationDisabled}
              onClick={() => void draft.goNext()}
              className="h-[18px] w-[18px] p-0 text-neutral-dim"
            >
              <ChevronRight className="size-3" />
            </Button>
            {/* The dock takes the composer's place while a question is pending, so the stop the
                composer would have offered has to be offered here or a parked run cannot end. */}
            {stop ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Stop response"
                disabled={stop.disabled || stop.loading || controlsDisabled}
                onClick={stop.onStop}
                className="h-[22px] w-[22px] p-0 text-neutral-dim"
              >
                {stop.loading ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <Square className="size-3 fill-current" />
                )}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss questions"
              disabled={controlsDisabled}
              onClick={() => void draft.dismiss()}
              className="h-[22px] w-[22px] p-0 text-neutral-dim"
            >
              <X className="size-3.5" />
            </Button>
          </div>
          <div className="flex w-full items-baseline gap-1.5">
            <span className="shrink-0 text-[14px] font-medium text-neutral-dim">
              {questionIndex + 1}.
            </span>
            <h3 className="min-w-0 break-words text-[14px] font-semibold leading-snug text-foreground">
              <InlineReferenceMarkdown content={currentQuestion.question} />
            </h3>
          </div>
          {draft.isClosed ? (
            <output className="px-5 text-[12px] text-muted-foreground">
              This question is no longer active.
            </output>
          ) : null}
        </div>

        {/* Keyed per question so stepping to the next one starts at its first option instead
            of wherever the previous question's answers were scrolled to. */}
        <ScrollShadow
          key={currentQuestion.id}
          className="flex-initial"
          colorScheme="transparent"
          contentClassName="flex flex-col gap-0.5"
          viewportProps={{ "data-testid": "assistant-ask-user-answers" }}
        >
          {currentQuestion.options.map((option, index) => {
            const selected =
              currentAnswer?.kind === "option" && currentAnswer.optionId === option.id;
            return (
              <button
                key={option.id}
                type="button"
                className={cn(
                  OPTION_ROW_CLASS,
                  selected ? "bg-background" : "bg-transparent hover:bg-background/60"
                )}
                disabled={navigationDisabled}
                onClick={() => void draft.selectOption(currentQuestion.id, option.id)}
                data-testid="assistant-ask-user-option"
              >
                <OptionLetterBadge
                  letter={getOptionLetter(index)}
                  tone={selected ? "selected" : "muted"}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "break-words text-foreground",
                        hasDescriptions ? "text-[14px] font-semibold" : "text-[13px] font-normal"
                      )}
                    >
                      {/* The option sits inside a button, so its chips are static: a link
                          inside a button breaks both the click target and the a11y tree. */}
                      <InlineReferenceMarkdown content={option.label} mentions="static" />
                    </span>
                    {option.recommended ? (
                      <span className="text-[11px] font-medium text-primary">Recommended</span>
                    ) : null}
                  </span>
                  {option.description ? (
                    <span className="block break-words text-[12px] font-normal leading-normal text-muted-foreground">
                      <InlineReferenceMarkdown content={option.description} mentions="static" />
                    </span>
                  ) : null}
                </span>
                <ArrowRight className="size-3.5 shrink-0 text-neutral-dim" />
              </button>
            );
          })}

          {allowCustom ? (
            <div
              className={cn(
                "flex w-full gap-3.5 border border-transparent p-3",
                customComposerActive ? "items-start" : "items-center"
              )}
              data-testid="assistant-ask-user-custom"
            >
              <OptionLetterBadge
                letter={getOptionLetter(currentQuestion.options.length)}
                tone={customSelected ? "selected" : "muted"}
              />
              <div className="relative min-w-0 flex-1">
                {customComposerActive ? (
                  <MessageRichTextInput
                    key={currentQuestion.id}
                    placeholder={customAnswerPlaceholder}
                    initialMarkdown={draft.composerSeed?.markdown ?? customDraft.markdown}
                    disabled={controlsDisabled}
                    isSubmitting={draft.isResolving}
                    onSubmit={submitCustomAnswer}
                    onContentChange={handleCustomContentChange}
                    onBlur={() => void draft.flushCustom(currentQuestion.id)}
                    autoFocus
                    editorTestId="assistant-ask-user-custom-editor"
                    submitAriaLabel="Submit custom answer"
                    mentionProjectId={projectId}
                    voiceInput={
                      voiceInput
                        ? {
                            ...voiceInput,
                            onPhaseChange: (phase) => {
                              setVoiceBusy((previous) => {
                                if (phase === "idle") {
                                  return previous?.requestId === request.requestId &&
                                    previous.questionId === currentQuestion.id
                                    ? null
                                    : previous;
                                }
                                return {
                                  requestId: request.requestId,
                                  questionId: currentQuestion.id,
                                };
                              });
                              voiceInput.onPhaseChange?.(phase);
                            },
                          }
                        : undefined
                    }
                    features={{
                      mentions: true,
                      emojiReplacement: false,
                      internalLinkResolution: true,
                    }}
                    toolbarPreset="none"
                    enterKeyBehavior="enterToSend"
                    composerLayout
                    composerLeadingAction="none"
                    hideSubmitAction
                    showFooter={false}
                    characterLimit={{
                      limit: ASK_USER_CUSTOM_ANSWER_MAX,
                      warningThreshold: ASK_USER_CUSTOM_ANSWER_WARNING_RATIO,
                    }}
                    className="border-primary bg-background shadow-none focus-within:border-primary"
                  />
                ) : (
                  <Textarea
                    id={customInputId}
                    value={customDraft.plainText}
                    onFocus={handleCustomFocus}
                    onClick={handleCustomFocus}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" || event.shiftKey) {
                        return;
                      }
                      event.preventDefault();
                      void submitCustomAnswer();
                    }}
                    readOnly
                    disabled={controlsDisabled}
                    placeholder={customAnswerPlaceholder}
                    aria-label={`Custom answer for ${currentQuestion.question}`}
                    className={cn(
                      "min-h-9 rounded-none border pr-16 text-[13px] shadow-none placeholder:text-neutral-dim",
                      customSelected
                        ? "border-primary bg-background focus-visible:border-primary focus-visible:ring-primary/20"
                        : "border-border bg-transparent"
                    )}
                  />
                )}
                {!customComposerActive && customSelected ? (
                  <span className="pointer-events-none absolute right-3 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 text-[11px] font-medium text-neutral-dim">
                    <CornerDownLeft className="size-3" />
                    Enter
                  </span>
                ) : null}
                {showCustomLimit ? (
                  <span
                    className={cn(
                      "mt-1 block text-right text-[11px] font-medium",
                      customLimitExceeded ? "text-destructive" : "text-muted-foreground"
                    )}
                  >
                    {customAnswerLength.toLocaleString()} /{" "}
                    {ASK_USER_CUSTOM_ANSWER_MAX.toLocaleString()}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </ScrollShadow>
      </div>
    </fieldset>
  );
}
